import { Badge } from "@thilke/ui/components/badge";
import { Button } from "@thilke/ui/components/button";
import { Counter } from "@thilke/ui/components/counter";
import { EmptyState } from "@thilke/ui/components/empty-state";
import { Panel } from "@thilke/ui/components/panel";
import { ScrollArea } from "@thilke/ui/components/scroll-area";
import { Separator } from "@thilke/ui/components/separator";
import { Skeleton } from "@thilke/ui/components/skeleton";
import { Slider } from "@thilke/ui/components/slider";
import { Toolbar, ToolbarGroup } from "@thilke/ui/components/toolbar";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@thilke/ui/components/ui/collapsible";
import { Toggle } from "@thilke/ui/components/ui/toggle";
import {
  ToggleGroup,
  ToggleGroupItem,
} from "@thilke/ui/components/ui/toggle-group";
import {
  Archive,
  Crosshair,
  Grid3X3,
  Layers3,
  MousePointer2,
  Settings2,
} from "lucide-react";

const scrollAreaItems = Array.from(
  { length: 24 },
  (_, i) => `Component #${i + 1}`,
);

export function SliderDemo() {
  return (
    <div className="grid w-full max-w-sm gap-4">
      <Slider defaultValue={[33]} max={100} step={1} />
      <Slider defaultValue={[66]} max={100} step={1} />
    </div>
  );
}
export function SkeletonDemo() {
  return (
    <div className="grid w-full max-w-sm gap-3">
      <Skeleton className="h-4 w-2/3" />
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-5/6" />
    </div>
  );
}
export function SeparatorDemo() {
  return (
    <div className="w-full max-w-sm">
      <div className="space-y-1">
        <h4 className="text-sm font-medium">Radix Primitives</h4>
        <p className="text-sm text-muted-foreground">
          An open-source UI component library.
        </p>
      </div>
      <Separator className="my-4" />
      <div className="flex h-5 items-center gap-4 text-sm">
        <span>Blog</span>
        <Separator orientation="vertical" />
        <span>Docs</span>
        <Separator orientation="vertical" />
        <span>Source</span>
      </div>
    </div>
  );
}
export function ScrollAreaDemo() {
  return (
    <ScrollArea className="h-40 w-72 rounded-md border">
      <div className="p-4">
        {scrollAreaItems.map((item) => (
          <div key={item} className="py-1 text-sm">
            {item}
          </div>
        ))}
      </div>
    </ScrollArea>
  );
}
export function ToggleDemo() {
  return (
    <div className="flex flex-wrap gap-2">
      <Toggle>Bold</Toggle>
      <Toggle variant="outline">Italic</Toggle>
      <Toggle defaultPressed>Pressed</Toggle>
    </div>
  );
}
export function ToggleGroupDemo() {
  return (
    <ToggleGroup type="single" defaultValue="center">
      <ToggleGroupItem value="left">Left</ToggleGroupItem>
      <ToggleGroupItem value="center">Center</ToggleGroupItem>
      <ToggleGroupItem value="right">Right</ToggleGroupItem>
    </ToggleGroup>
  );
}
export function CollapsibleDemo() {
  return (
    <Collapsible defaultOpen className="w-full max-w-sm space-y-2">
      <CollapsibleTrigger asChild>
        <Button variant="outline" className="w-full justify-between">
          Open details <Badge variant="secondary">3</Badge>
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <Panel position="docked" size="md">
          Reusable details region using Radix Collapsible.
        </Panel>
      </CollapsibleContent>
    </Collapsible>
  );
}
export function EmptyStateDemo() {
  return (
    <EmptyState
      icon={Archive}
      title="No results"
      description="Clear filters or add a registry item."
    />
  );
}
export function PanelDemo() {
  return (
    <div className="grid w-full max-w-lg grid-cols-2 gap-3">
      <Panel position="docked" size="md">
        Docked panel
      </Panel>
      <Panel position="docked" size="md">
        Raised content
      </Panel>
    </div>
  );
}
export function ToolbarDemo() {
  return (
    <Toolbar orientation="horizontal">
      <ToolbarGroup>
        <Button variant="ghost" size="icon">
          <MousePointer2 />
        </Button>
        <Button variant="ghost" size="icon">
          <Crosshair />
        </Button>
        <Button variant="ghost" size="icon">
          <Grid3X3 />
        </Button>
      </ToolbarGroup>
      <Separator orientation="vertical" className="mx-0.5 h-5" />
      <ToolbarGroup>
        <Button variant="ghost" size="icon">
          <Layers3 />
        </Button>
        <Button variant="ghost" size="icon">
          <Settings2 />
        </Button>
      </ToolbarGroup>
    </Toolbar>
  );
}

export function CounterDemo() {
  return (
    <div className="grid w-full max-w-sm gap-4">
      <Counter value={3} max={8} label="Heat" tone="warning" />
      <Counter
        value={2}
        max={4}
        label="Structure"
        tone="destructive"
        size="lg"
      />
      <Counter value={5} max={6} label="Countdown clock" tone="info" />
    </div>
  );
}
